import * as React from "react";
import { useState, useRef, useEffect } from "react";
import {
  makeStyles,
  Image,
  Text,
  Input,
  Label,
  Button,
  Link,
  Divider,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
} from "@fluentui/react-components";
import { SiGoogle, SiLinkedin } from "react-icons/si";
import { DismissRegular } from "@fluentui/react-icons";

const brandColor = "#921784";

// --- Styles ---
const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    padding: "20px",
    boxSizing: "border-box",
    gap: "20px",
  },
  logo: {
    width: "150px",
    height: "auto",
    marginBottom: "10px",
  },
  title: {
    fontSize: "20px",
    fontWeight: "600",
    color: brandColor,
  },
  socialButton: {
    width: "100%",
    "> span": {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
    },
  },
  dividerContainer: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    gap: "8px",
    color: "#605e5c",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: "15px",
  },
  inputContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  forgotPasswordLink: {
    alignSelf: "flex-end",
    fontSize: "12px",
  },
  errorText: {
    color: "#d13438",
    fontSize: "12px",
    textAlign: "center",
    marginTop: "5px",
  },
  signUpContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "4px",
    marginTop: "10px",
    fontSize: "14px",
  },
  signUpLink: {
  },
  messageBarContainer: {
    width: '100%',
    maxWidth: '300px',
    marginBottom: '15px',
  }
});

// --- Props interface ---
export interface LoginScreenProps {
  onEmailLoginSubmit: (email: string, password: string) => Promise<void>;
  onGoogleLogin: () => void;
  onLinkedInLogin: () => void;
  onRegisterLinkClick: () => void;
  onForgotPassword: () => void;
  isLoading?: boolean;
  error?: string | null;
  clearError?: () => void;
}

// --- Component LoginScreen ---
const LoginScreen: React.FC<LoginScreenProps> = ({
  onEmailLoginSubmit,
  onGoogleLogin,
  onLinkedInLogin,
  onRegisterLinkClick,
  onForgotPassword,
  isLoading,
  error,
  clearError
}) => {
  const styles = useStyles();
  const [email, setEmail] = useState(() => {
    const saved = localStorage.getItem('temp_login_email');
    return saved || "";
  });
  const [password, setPassword] = useState(() => {
    const saved = localStorage.getItem('temp_login_password');
    return saved || "";
  });
  const preserveFieldsRef = useRef({ email: "", password: "" });
  const loginAttemptRef = useRef(false);

  const emailId = React.useId();
  const passwordId = React.useId();

  useEffect(() => {
    if (error && preserveFieldsRef.current.email && !loginAttemptRef.current) {
      setEmail(preserveFieldsRef.current.email);
      setPassword(preserveFieldsRef.current.password);
    }
  }, [error]);

  useEffect(() => {
    if (isLoading === false && error && preserveFieldsRef.current.email) {
      setEmail(preserveFieldsRef.current.email);
      setPassword(preserveFieldsRef.current.password);
    }
  }, [isLoading, error]);

  const handleEmailChange = (_e: any, data: { value: string }) => {
    if (error && clearError) clearError();
    setEmail(data.value);
    localStorage.setItem('temp_login_email', data.value);
    preserveFieldsRef.current.email = data.value;
  };
  const handlePasswordChange = (_e: any, data: { value: string }) => {
    if (error && clearError) clearError();
    setPassword(data.value);
    localStorage.setItem('temp_login_password', data.value);
    preserveFieldsRef.current.password = data.value;
  };

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isLoading) return;
    
    preserveFieldsRef.current = { email, password };
    loginAttemptRef.current = true;
    
    try {
      await onEmailLoginSubmit(email, password);
      setEmail("");
      setPassword("");
      localStorage.removeItem('temp_login_email');
      localStorage.removeItem('temp_login_password');
      preserveFieldsRef.current = { email: "", password: "" };
      loginAttemptRef.current = false;
    } catch (error) {
      loginAttemptRef.current = false;
    }
  };

  // Handler for register event
  const handleRegisterLink = () => {
    console.log("Enlace Registrarse presionado -> Abrir sitio web");
    onRegisterLinkClick();
  };

  return (
    <div className={styles.container}>
      <Image className={styles.logo} src="/assets/lexfania-logo.svg" alt="Logo de Lexfania" />

      <Text as="h2" className={styles.title}>
        Iniciar Sesión
      </Text>

      {/* Error Messages */}
      {error && (
        <div className={styles.messageBarContainer}>
          <MessageBar intent="error">
              <MessageBarBody>{error}</MessageBarBody>
              <MessageBarActions>
                  <Button appearance="transparent" icon={<DismissRegular />} aria-label="Cerrar mensaje" onClick={clearError} />
              </MessageBarActions>
          </MessageBar>
        </div>
      )}

      {/* Social Buttons */}
      <Button appearance="outline" className={styles.socialButton} icon={<SiGoogle />} onClick={onGoogleLogin} disabled={isLoading}>
        Inicia sesión con Google
      </Button>
      <Button appearance="outline" className={styles.socialButton} icon={<SiLinkedin />} onClick={onLinkedInLogin} disabled={isLoading}>
        Inicia sesión con LinkedIn
      </Button>

      <div className={styles.dividerContainer}>
        <Divider style={{ flexGrow: 1 }} /> <Text size={200}>O</Text> <Divider style={{ flexGrow: 1 }} />
      </div>

      {/* Form Email/Password */}
      <form onSubmit={handleFormSubmit} className={styles.form}>
        <div className={styles.inputContainer}>
          <Label htmlFor={emailId} required>Correo</Label>
          <Input type="email" id={emailId} value={email} onChange={handleEmailChange} required placeholder="ejemplo@email.com" disabled={isLoading} />
        </div>
        <div className={styles.inputContainer}>
          <Label htmlFor={passwordId} required>Contraseña</Label>
          <Input type="password" id={passwordId} value={password} onChange={handlePasswordChange} required placeholder="Contraseña" disabled={isLoading} />
          <Link onClick={onForgotPassword} className={styles.forgotPasswordLink} disabled={isLoading}>
            Olvidé mi contraseña
          </Link>
        </div>
        <Button appearance="primary" type="submit" disabled={isLoading} icon={isLoading ? <Spinner size="tiny" /> : undefined}>
          {isLoading ? "Iniciando..." : "Iniciar sesión"}
        </Button>
      </form>

      {/* Sign Up Link */}
      {/*
      TODO: currently disabled since it is broken
      <div className={styles.signUpContainer}>
        <Text size={300}>¿Eres nuevo?</Text>
        <Link onClick={handleRegisterLink} className={styles.signUpLink} disabled={isLoading}>Regístrate en la web</Link>
      </div> */}
    </div>
  );
};

export default LoginScreen;
